/**
 * The audit log viewer.
 *
 * Rows have been written since the first admin change and nothing has ever read
 * them. The screen is deliberately plain — a reverse-chronological list, day
 * rules, one line per event and the changed fields underneath — because the
 * question it answers is always the same: *who changed what, and when.*
 *
 * Two decisions worth keeping:
 *  - The event filter is built from the events actually present, so a new
 *    module's events appear without anyone editing a list here.
 *  - Paging is by cursor, appending. A log where "next page" can skip a row
 *    written in the same millisecond is not evidence of anything.
 */

import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ScrollText, TriangleAlert } from 'lucide-react';
import { apiGet } from '../api/client';
import { Badge, Button, Card } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { formatDate, formatRelative } from '../lib/format';
import {
  actorLabel,
  eventLabel,
  groupByDay,
  summarise,
  toneFor,
  type ApiAuditEntry,
  type AuditPage,
} from '../lib/audit/entries';

interface EventCount {
  event: string;
  count: number;
}

export function AuditLogPage() {
  const [event, setEvent] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const events = useQuery({
    queryKey: ['audit-events'],
    queryFn: () => apiGet<EventCount[]>('/api/audit/events'),
  });

  // The filter is part of the key, so changing it starts a fresh set of pages
  // rather than paging through a list that no longer exists.
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['audit', event],
      initialPageParam: null as string | null,
      queryFn: ({ pageParam }) => {
        const params = new URLSearchParams();
        if (event) params.set('event', event);
        if (pageParam) params.set('cursor', pageParam);
        return apiGet<AuditPage>(`/api/audit?${params.toString()}`);
      },
      getNextPageParam: (last) => last.nextCursor,
    });

  const entries = useMemo<ApiAuditEntry[]>(
    () => (data?.pages ?? []).flatMap((page) => page.entries),
    [data],
  );
  const groups = useMemo(() => groupByDay(entries), [entries]);

  if (error) {
    return (
      <div className="mx-auto max-w-[700px] px-5 py-12 text-center">
        <TriangleAlert size={26} strokeWidth={1.5} className="mx-auto mb-3 text-stop-700" aria-hidden />
        <p className="text-sm text-ink-700">
          {error instanceof Error ? error.message : 'Could not load the audit log.'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <FilterChip label="All events" active={event === ''} onClick={() => setEvent('')} />
        {(events.data ?? []).map((option) => (
          <FilterChip
            key={option.event}
            label={eventLabel(option.event)}
            count={option.count}
            active={event === option.event}
            onClick={() => setEvent(option.event)}
          />
        ))}
      </div>

      {isLoading ? (
        <p className="px-5 py-12 text-center text-sm text-ink-400">Loading…</p>
      ) : groups.length === 0 ? (
        <EmptyLog filtered={event !== ''} />
      ) : (
        <Card>
          {groups.map((group) => (
            <section key={group.day}>
              <h2 className="border-b border-line bg-surface-sunken px-4 py-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-500">
                {formatDate(group.day)}
              </h2>
              <ul>
                {group.entries.map((entry) => (
                  <li key={entry.id} className="border-b border-line last:border-b-0">
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === entry.id ? null : entry.id)}
                      aria-expanded={expanded === entry.id}
                      className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors duration-fast hover:bg-surface-sunken"
                    >
                      <Badge tone={toneFor(entry.event)} className="mt-0.5 shrink-0">
                        {eventLabel(entry.event)}
                      </Badge>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-ink-700">
                          {summarise(entry)}
                        </span>
                        <span className="block text-2xs text-ink-400">
                          {actorLabel(entry)}
                          {entry.brand ? ` · ${entry.brand.name}` : ' · all brands'}
                          {entry.entityType ? ` · ${entry.entityType}` : ''}
                        </span>
                      </span>

                      <span
                        className="shrink-0 text-2xs text-ink-400"
                        title={new Date(entry.createdAt).toISOString()}
                      >
                        {formatRelative(entry.createdAt)}
                      </span>
                    </button>

                    {expanded === entry.id ? <Detail entry={entry} /> : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {hasNextPage ? (
            <div className="px-4 py-3 text-center">
              <Button size="sm" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Loading…' : 'Load older'}
              </Button>
            </div>
          ) : (
            <p className="px-4 py-3 text-center text-2xs text-ink-400">
              That is the whole log for this filter.
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

function Detail({ entry }: { entry: ApiAuditEntry }) {
  return (
    <div className="border-t border-line bg-surface-sunken px-4 py-3">
      {entry.changes.length === 0 ? (
        <p className="text-xs text-ink-400">
          This event recorded no before/after payload. It happened; what it touched was not captured.
        </p>
      ) : (
        <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-[auto_1fr]">
          {entry.changes.map((change) => (
            <div key={change.field} className="contents">
              <dt className="font-mono text-ink-500">{change.field}</dt>
              <dd className="tabular text-ink-700">
                {change.before === null ? (
                  <span className="text-ink-400">not set</span>
                ) : (
                  <span className="line-through decoration-ink-300">{change.before}</span>
                )}
                <span className="mx-1.5 text-ink-300">→</span>
                {change.after === null ? (
                  <span className="text-ink-400">removed</span>
                ) : (
                  <span className="font-medium">{change.after}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {entry.entityId ? (
        <p className="mt-2 font-mono text-2xs text-ink-400">
          {entry.entityType}: {entry.entityId}
        </p>
      ) : null}
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors duration-fast',
        active ? 'bg-ink-900 text-paper' : 'bg-surface text-ink-500 shadow-hairline hover:text-ink-900',
      )}
    >
      {label}
      {count !== undefined ? <span className="ml-1.5 opacity-60">{count}</span> : null}
    </button>
  );
}

function EmptyLog({ filtered }: { filtered: boolean }) {
  return (
    <Card>
      <div className="px-4 py-12 text-center">
        <ScrollText size={28} strokeWidth={1.5} className="mx-auto mb-3 text-ink-300" aria-hidden />
        <p className="text-sm text-ink-700">
          {filtered ? 'Nothing with that event.' : 'The audit log is empty.'}
        </p>
        {!filtered ? (
          <p className="mx-auto mt-2 max-w-[540px] text-sm text-ink-400">
            Rows are written when someone changes a brand, grants access, approves or rejects a
            write, or ingests a knowledge-base document. Nothing has done any of those yet.
          </p>
        ) : null}
      </div>
    </Card>
  );
}
